import {
  Box,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { SWTextField } from 'components/Form/Input/SWTextField';
import { InteractiveModal } from 'components/Modals/InteractiveModal/BaseModal';
import { type Role } from 'components/Permission/permission.types';
import ExternalUserHandler from 'handlers/external.user.handler';
import { useMemo, useState } from 'react';
import { useAppSelector } from 'store/hook';
import { modalInputFieldStyle } from 'styles/global.css';
import { type IUpdateCustomerPayload } from 'types/payloads/customer.payload.types';
import { type BaseModalProps } from 'types/props.base';
import { updateCustomerRequestSchema } from 'utils/schemas/customer.schema';
import { useFormIsValid } from '../../useFormIsValidHook';
import SwitchToggle from 'components/Switch';
import { isDarkTheme } from 'utils/theme';
import RuleHandler from 'handlers/rule.handler';
import FleetOverviewHandler from 'handlers/fleetOverView.handler';
import NotificationHandler from 'handlers/notification.handler';
import TagHandler from 'handlers/tag.handler';
import AssetHandler from 'handlers/asset.handler';
import { useNavigate } from 'react-router-dom';
import { mergeNavigationWithDbConfig } from 'components/Route/utils';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

export const EditCustomerContactModal = (props: BaseModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const externalUserHandler = new ExternalUserHandler();
  const ruleHandler = new RuleHandler();
  const fleetOverviewHandler = new FleetOverviewHandler();
  const tagHandler = new TagHandler();

  const notificationHandler = new NotificationHandler();
  const assetHandler = new AssetHandler();

  const { customer, role } = useAppSelector((state: any) => state.authReducer);
  const theme = useAppSelector((state) => state.authReducer).customer.theme;
  const enableDarkTheme = isDarkTheme(theme);
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const [customerUnitSystem, setCustomerUnitSystem] = useState(
    customer.unitSystem
  );

  const [open, setOpen] = useState(false);

  const [customerPayload, setCustomerPayload] =
    useState<IUpdateCustomerPayload>({
      contactFirstName: customer.contactFirstName,
      contactLastName: customer.contactLastName,
      customerName: customer.name,
      email: customer.email,
      phoneNumber: customer.phoneNumber,
      internalAccountingLink:
        customer.internalAccountingLink === null
          ? ''
          : customer.internalAccountingLink,
      unitSystem: customer.unitSystem,
      preferredTimeDisplayMode: customer.preferredTimeDisplayMode,
    });

  const tireModules = ['tireAnalytics', 'tireInsights', 'tireManagement'];

  const navigationConfigData = useAppSelector(
    (state) => state.navigationConfigReducer.data
  );

  const mergedStaticAndDbConfig = useMemo(() => {
    const temp = mergeNavigationWithDbConfig(navigationConfigData);
    return temp;
  }, [navigationConfigData]);

  const enabledForList = tireModules.flatMap(
    (id) =>
      mergedStaticAndDbConfig.find((item) => item.id === id)?.enabledFor ?? []
  );

  const isTpmSEnabled = enabledForList.includes(customer?.code);

  // Validate form as user fills out fields
  const isValidForm = useFormIsValid(
    customerPayload,
    updateCustomerRequestSchema
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setCustomerPayload({
      ...customerPayload,
      [name]: value,
    });
  };

  const handleSuccess = async () => {
    await externalUserHandler.updateCurrentCustomer({
      ...customerPayload,
      unitSystem: customerUnitSystem,
    });
    if (customerUnitSystem === customer.unitSystem) {
      void Promise.all([
        ruleHandler.getAll(),
        fleetOverviewHandler.getAll(),
        tagHandler.getAll(),
        notificationHandler.getAllLatest(),
        assetHandler.getAll(),
        assetHandler.getAssetsWithExcessiveIdling(),
      ]);
      props.handleAbort();
    } else {
      setOpen(true);
    }
  };

  const handleClose = () => {
    navigate('/fleet/summary');
  };

  return (
    <InteractiveModal
      initialState={props.open}
      SuccessButtonProps={{
        disabled: !isValidForm,
        onClick: handleSuccess,
        label: t('save_edits'),
      }}
      cancelButtonText={t('cancel')}
      handleAbort={props.handleAbort}
      title={props.title}
      confirmCancellation
      enableDarkTheme={enableDarkTheme}
    >
      <CustomerNameInput
        role={role}
        customerPayload={customerPayload}
        handleChange={handleChange}
        enableDarkTheme={enableDarkTheme}
      />
      {/** Dialog Spot */}
      <Dialog
        open={open}
        disableEscapeKeyDown // Prevent closing with Escape key
        onClose={() => {}} // No-op function to disable close on outside click
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        BackdropProps={{
          style: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        }}
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: enableDarkTheme
              ? palette.boxBackground
              : palette.boxBackground,
            color: palette.textPrimary,
          },
        }}
      >
        <DialogTitle id="alert-dialog-title">
          {t('app_reload_required')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            sx={{
              color: palette.textPrimary,
            }}
          >
            {t('you_have_updated_the_unit_system_the_app_will_reload_to_appl')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            sx={{
              color: palette.textPrimary,
              backgroundColor: palette.primary,
              paddingLeft: '15px',
              paddingRight: '15px',
            }}
          >
            {t('reload_now')}
          </Button>
        </DialogActions>
      </Dialog>

      {/** End of dialog */}

      <Grid2 xs={12} lg={12} padding={'15px'} key="text-block">
        <Box
          sx={{
            height: '35px',
            backgroundColor: '#7485A2',
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '14px',
            paddingTop: '8px',
            paddingBottom: '8px',
          }}
        >
          <Typography color="white" variant="bodyBold">
            {t('company_contact_information')}
          </Typography>
        </Box>
      </Grid2>

      {/* First Name TextField */}
      <SWTextField
        key="firstName"
        name="firstName"
        label={t('first_name')}
        TextFieldProps={{
          name: 'contactFirstName',
          value: customerPayload.contactFirstName,
          style: { minWidth: '258px' },
          onChange: handleChange,
          InputProps: {
            sx: modalInputFieldStyle,
          },
        }}
        enableDarkTheme={enableDarkTheme}
      />

      {/* Last name input */}
      <SWTextField
        key="lastName"
        name="lastName"
        label={t('last_name')}
        TextFieldProps={{
          name: 'contactLastName',
          value: customerPayload.contactLastName,
          style: { minWidth: '258px' },
          onChange: handleChange,
          InputProps: {
            sx: modalInputFieldStyle,
          },
        }}
        enableDarkTheme={enableDarkTheme}
      />

      {/* Email input */}
      <SWTextField
        key="email"
        name="email"
        label={t('email')}
        TextFieldProps={{
          name: 'email',
          value: customerPayload.email,
          style: { minWidth: '258px' },
          onChange: handleChange,
          InputProps: {
            sx: modalInputFieldStyle,
          },
        }}
        enableDarkTheme={enableDarkTheme}
      />
      {/* Phone number input */}
      <SWTextField
        key="phoneNumber"
        name="phoneNumber"
        label={t('phone_number')}
        TextFieldProps={{
          name: 'phoneNumber',
          value: customerPayload.phoneNumber,
          style: { minWidth: '258px' },
          onChange: handleChange,
          InputProps: {
            sx: modalInputFieldStyle,
          },
        }}
        enableDarkTheme={enableDarkTheme}
      />
      <SWTextField
        key="internalAccountingLink"
        name="internalAccountingLink"
        label={t('internal_accounting_link')}
        TextFieldProps={{
          name: 'internalAccountingLink',
          value: customerPayload.internalAccountingLink,
          style: { minWidth: '258px' },
          onChange: handleChange,
          InputProps: {
            sx: modalInputFieldStyle,
          },
        }}
        enableDarkTheme={enableDarkTheme}
      />
      <Box>
        <Typography
          variant="bodyBold"
          style={{ fontSize: '14px' }}
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('unit_system_2')}
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          key="unit-system"
          mt="8px"
        >
          <Typography
            variant="body2"
            style={{
              fontSize: '12px',
              opacity: customerUnitSystem === 'imperial' ? '1' : '.5',
              color:
                customerUnitSystem === 'imperial'
                  ? palette.primary
                  : palette.grey,
            }}
            key="imperial"
          >
            {t('imperial')}
          </Typography>
          <SwitchToggle
            checked={customerUnitSystem === 'metric'}
            onChange={(e) => {
              e.target.checked
                ? setCustomerUnitSystem('metric')
                : setCustomerUnitSystem('imperial');
            }}
            inputProps={{ 'aria-label': t('unit_system_2') }}
          />
          <Typography
            variant="body2"
            style={{
              fontSize: '12px',
              color:
                customerUnitSystem === 'metric'
                  ? palette.primary
                  : palette.grey,
              opacity: customerUnitSystem === 'metric' ? '1' : '.5',
            }}
            key="metric"
          >
            {t('metric')}
          </Typography>
        </Stack>
      </Box>
      <Box>
        <Typography
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('additional_modules')}
        </Typography>
        <Typography
          sx={{
            pt: 2,
            color: palette.textPrimary,
          }}
        >
          {t('x_tires')}{' '}
          <span
            style={{
              color: isTpmSEnabled ? '#749A4A' : '#A03434',
              fontWeight: '700',
            }}
          >
            {isTpmSEnabled ? t('available_status') : t('not_available')}
          </span>
        </Typography>
      </Box>
    </InteractiveModal>
  );
};

/**
 * Conditionally renders the Customer name component.
 * - Directors will see a text field
 * - Non-directors will see a constant text element
 */
function CustomerNameInput(props: {
  role: Role | null;
  customerPayload: IUpdateCustomerPayload;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  enableDarkTheme: boolean;
}) {
  const { t } = useTranslation();
  if (props.role === 'director') {
    return (
      <SWTextField
        key="customerName"
        name="customerName"
        label={t('customer_name')}
        TextFieldProps={{
          name: 'customerName',
          value: props.customerPayload.customerName,
          style: { minWidth: '258px' },
          onChange: props.handleChange,
          InputProps: {
            sx: modalInputFieldStyle,
          },
        }}
        enableDarkTheme={props.enableDarkTheme}
      />
    );
  }

  const palette = props.enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  return (
    <Box>
      <Typography variant="bodyBold" sx={{ color: palette.textPrimary }}>
        {t('company_name_2')}
      </Typography>
      <Typography sx={{ color: palette.textPrimary }}>
        {props.customerPayload.customerName}
      </Typography>
    </Box>
  );
}
